import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Clock, Airplane, Receipt, CheckSquare, UsersThree, WarningCircle, IdentificationCard, CurrencyInr, TrendUp, ChartBar } from "@phosphor-icons/react";
import { HrmsReport } from "@/components/hrms/HrmsReport";
import { MyIncrements } from "@/components/hr/Increments";
import Salary from "@/pages/Salary";
import { MyAttendance, MyLeave, MySlips } from "@/components/hrms/SelfService";
import { LeaveApprovals, TeamAttendance } from "@/components/hrms/Approvals";
import { EmployeeDocsTab } from "@/pages/Hrms";
import { useAuth } from "@/contexts/AuthContext";

export default function MyHrms() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "");
  const load = useCallback(async () => {
    try { setMe((await api.get("/hrms/me")).data); } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!me) return <div className="page-shell text-sm text-muted-foreground">Loading…</div>;

  const canPayroll = ["manager", "regional_manager", "hr"].includes(user?.role);
  const tabs = [
    ...(canPayroll ? [{ id: "payroll", label: "Salary & Payroll", icon: CurrencyInr }] : []),
    ...(["hr", "regional_manager"].includes(user?.role) ? [{ id: "employees", label: "Employees", icon: IdentificationCard }] : []),
    ...(me.employee ? [
      { id: "att", label: "My Attendance", icon: Clock },
      { id: "leave", label: "Apply Leave", icon: Airplane },
      { id: "slips", label: "Salary Slips", icon: Receipt },
      { id: "increments", label: "My Increments", icon: TrendUp },
    ] : []),
    ...(me.is_approver ? [
      { id: "approvals", label: "Leave Approvals", icon: CheckSquare },
      { id: "team", label: "Team Attendance", icon: UsersThree },
      { id: "report", label: "Report", icon: ChartBar },
    ] : []),
  ];
  const active = tab || tabs[0]?.id;

  return (
    <div className="space-y-2" data-testid="my-hrms-page">
    <div className="page-shell pb-0 space-y-5">
      <div>
        <div className="eyebrow">HRMS</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">{me.employee ? `Hi, ${me.employee.name}` : "HRMS"}</h1>
        <p className="text-sm text-muted-foreground mt-1">Attendance, leave and salary slips.</p>
      </div>
      {!me.employee && !me.is_approver && (
        <div className="card-flat p-4 text-[13px] flex items-center gap-2 border-amber-300 bg-amber-50 text-amber-900" data-testid="my-hrms-unlinked">
          <WarningCircle size={16} /> Your login is not linked to an employee record yet. Ask HR to add your login email to your employee profile in HRMS → Salary & Payroll → Employees.
        </div>
      )}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`my-hrms-tab-${t.id}`}
            className={`px-4 py-2 text-sm flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${active === t.id ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>
      {active === "employees" && <EmployeeDocsTab />}
      {active === "att" && <MyAttendance me={me} onChanged={load} />}
      {active === "leave" && <MyLeave />}
      {active === "slips" && <MySlips />}
      {active === "increments" && <MyIncrements />}
      {active === "approvals" && <LeaveApprovals />}
      {active === "team" && <TeamAttendance />}
      {active === "report" && <HrmsReport />}
    </div>
      {active === "payroll" && <Salary />}
    </div>
  );
}
